import React, { useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { Box, Stack } from '@mui/material'

import { Button, Tabs, TABS_CONTENT_GAP } from '@repo/ui'
import { Header } from 'components/navigation'
import { useCanAccess } from 'hooks'
import { MARKET_RESEARCH_TITLE, ROUTES } from 'routes'

import { MarketContentTab } from './MarketContentTab'
import { MarketSourcesTab } from '../InternalMarketSourcesPage/InternalMarketSourcesPage'
import { RescanAllFeedsButton } from '../CompetitorFeeds'
import { AskClaimsTab } from '../InternalMarketReviewPage/InternalMarketReviewPage'

/**
 * `/internal/content` — the market-data corpus (epic VRG-445).
 *
 * Renamed from `/internal/market`. The page was "Content" and its first tab "Library",
 * not "Content" — a tab repeating its own page title is the stacked-duplicate-header
 * shape §52 records, and the same collision the Staff -> Directory rename had to
 * resolve one surface over.
 *
 * VRG-738 (Spencer, 2026-10-22): the page is now "Market research"
 * (`MARKET_RESEARCH_TITLE`), so that reason is gone and the first tab is "Content" —
 * the plain name for what it lists. Tabs read Content · Claims · Sources. Only the
 * NAMES and the order changed: the tab values and URLs (`?tab=content|sources|stats`,
 * `/internal/content`, `/internal/content/sources`, `/internal/content/review`) are
 * as they were, so existing links keep landing on the same tab.
 *
 * ⚠️ The OLD `/internal/market*` paths no longer resolve. Nothing outside this repo
 * links to them (the surface shipped this week and had no palette row until now), so
 * they are not kept as redirects — but a link pasted in Slack before today is dead.
 *
 * ## One page, three tabs, because the three tables are one pipeline
 *
 * Sources, Content and Stats were built as three separate routes, and that was wrong
 * in a way that only shows up once you use it: each page's only route back to the
 * others was a secondary button in its own header, so landing on Sources from a link
 * left you with no way back to Content at all. Worse, it framed them as three
 * unrelated internal tools when they are three stages of one thing —
 *
 *   SOURCE (a feed)  1 -> N  CONTENT (an article, video, report)  1 -> N  STATS
 *
 * — with content also addable by hand, and only APPROVED stats ever reaching Ask Vera.
 * Tabs put the pipeline on one surface, which is the actual mental model rather than
 * three coincidental lists. The ORDER is not the pipeline's, by decision (Spencer,
 * 2026-10-22): Content and Claims lead, Sources is rightmost. Our reading, not his
 * stated reason: the two lists you work daily come first, and the feed registry —
 * configured once, revisited when a feed breaks — sits at the end.
 *
 * ## The primary action is per-tab, not per-page
 *
 * `Tabs` renders `rightComponents` on the same line as the tab list, which is exactly
 * the affordance a per-tab action wants: "Add content" on Content, "Add source" on
 * Sources, and nothing on Stats — a stat is never authored, only extracted and then
 * approved, so an Add button there would offer a capability that does not exist.
 *
 * ## Why the tab is in the URL
 *
 * `?tab=` rather than component state, so a tab is linkable and survives a reload. The
 * sibling routes (`/internal/content/sources`, `/internal/content/review`) resolve here
 * with the matching tab preselected rather than 404ing — they are referenced by
 * `internalAccess.ts`, which gates the Claims tab on a stricter permission than the page.
 */

type MarketTab = 'content' | 'sources' | 'stats'

/** Content · Claims · Sources, in that order (VRG-738; pinned by market-research-naming.guard). */
const TABS: { label: string; value: MarketTab }[] = [
  { label: 'Content', value: 'content' },
  /*
    "Claims", not "Stats". Not every extracted row is a statistic — `value` and `unit`
    are nullable, and the unit of the thing is a quoted CLAIM which may or may not carry
    a number. "Stats" quietly promised a figure in every row and was wrong whenever one
    had none. The tab, the table's own column and the detail modal all use the same word,
    so a reader is never asked to work out that three names mean one thing.

    The storage agrees: the table was renamed `market_stats` -> `ask_claims` (migration
    0104) rather than left to disagree with the interface. The Ask TOOL is still called
    `market_stats`, and that is deliberate — a tool name is the model's vocabulary for
    what it is asking FOR, not a pointer at a table (the `query` tool reads a dozen
    tables and is named after none of them).
  */
  { label: 'Claims', value: 'stats' },
  { label: 'Sources', value: 'sources' },
]

/** Which tab a path/query resolves to, including the two legacy routes. */
const tabFromLocation = (pathname: string, search: string): MarketTab => {
  if (pathname.startsWith(ROUTES.INTERNAL_MARKET_SOURCES)) return 'sources'
  if (pathname.startsWith(ROUTES.INTERNAL_MARKET_REVIEW)) return 'stats'
  const requested = new URLSearchParams(search).get('tab')
  return requested === 'sources' || requested === 'stats' ? requested : 'content'
}

export const InternalMarketPage = () => {
  const navigate = useNavigate()
  const { pathname, search } = useLocation()

  const tab = tabFromLocation(pathname, search)

  // Stats is the approval queue, and a proposal is unreviewed model output about a
  // third party's figures — so the population that can SEE one is the population that
  // can reject it. A whole surface, so hidden rather than disabled (§27).
  const { isHasAccess: canManage } = useCanAccess({ action: 'internal_market_intel_manage' })
  const visibleTabs = canManage ? TABS : TABS.filter(t => t.value !== 'stats')
  const activeTab = tab === 'stats' && !canManage ? 'content' : tab

  const [addContentOpen, setAddContentOpen] = useState(false)
  const [addSourceOpen, setAddSourceOpen] = useState(false)

  const setTab = (next: string) => {
    navigate(next === 'content' ? ROUTES.INTERNAL_MARKET : `${ROUTES.INTERNAL_MARKET}?tab=${next}`, {
      replace: true,
    })
  }

  /* One primary, rightmost (§26/§33) — and none on Stats, deliberately. */
  const tabAction =
    activeTab === 'content' ? (
      <Button variant="primary" onClick={() => setAddContentOpen(true)}>
        Add content
      </Button>
    ) : activeTab === 'sources' ? (
      // VRG-741 — competitor blog feeds are found automatically; this looks again for every due one.
      <Stack direction="row" spacing={3}>
        <RescanAllFeedsButton />
        <Button variant="primary" onClick={() => setAddSourceOpen(true)}>
          Add source
        </Button>
      </Stack>
    ) : null

  return (
    <Box>
      <Header title={MARKET_RESEARCH_TITLE} breadcrumbs={false} />

      <Tabs
        redirect={false}
        tabs={visibleTabs}
        value={activeTab}
        onChange={setTab}
        rightComponents={tabAction}
        sx={{ mb: `${TABS_CONTENT_GAP}px` }}
      />

      {activeTab === 'content' && (
        <MarketContentTab
          addOpen={addContentOpen}
          onOpenAdd={() => setAddContentOpen(true)}
          onCloseAdd={() => setAddContentOpen(false)}
        />
      )}
      {activeTab === 'sources' && (
        <MarketSourcesTab addOpen={addSourceOpen} onCloseAdd={() => setAddSourceOpen(false)} />
      )}
      {activeTab === 'stats' && <AskClaimsTab />}
    </Box>
  )
}
